import React, { useState, useEffect } from 'react';
import {
  authSessionService,
  companyService,
  userService,
} from './clients/ApiClients';
import { SidebarLayout } from './common/Catalyst/sidebar-layout';
import { Navbar } from './common/Catalyst/navbar';
import AppSidebar from './AppSidebar';
import { Button } from './common/Catalyst/button';
import { Heading } from './common/Catalyst/heading';
import { Divider } from './common/Catalyst/divider';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from './common/Catalyst/fieldset';
import { Input } from './common/Catalyst/input';
import ShowAlert from './ShowAlert';
import {
  UserUpdate,
  UserReturn,
  CompanyReturn,
} from './clients/rust-api-client';
import {
  getUserIdFromToken,
  getUserTypeFromToken,
} from './utils/TokenUtils';
import {
  canViewLedger,
  canManageBillingPolicy,
} from './constants/UserTypes';
import BillingPolicyForm from './billing/BillingPolicyForm';
import NoticeRuleList from './billing/NoticeRuleList';
import LoadingSpinner from './LoadingSpinner';
import { fresher, useSyncedCompany, useSyncedUser } from './sync/hooks';
import { PencilSquareIcon as PencilSquareIconOutline } from '@heroicons/react/24/outline';
import { PencilSquareIcon as PencilSquareIconSolid } from '@heroicons/react/24/solid';
import { ErrorAlert } from './common/Custom/ErrorAlert';
import { apiErrorMessage } from './utils/apiErrors';

interface SettingsPageProps {
  currentPage: string;
}

/**
 * A manager's own account: name, email address, and — where they have
 * one — their company's billing settings.
 *
 * **The Change Password card was removed 2026-09-12.** It had already
 * been narrowed to SuperAdmins, because WorkOS owns everybody else's
 * credential and the backend refuses `changePassword` for them. What
 * remained was the UI for the break-glass password (migration plan
 * §1.7) — and the break-glass is deliberately not reachable from the
 * product: `LoginPage` offers no password form either, so the only way
 * in by password is with a tool, and the same is now true of setting
 * one. The endpoint stays, gated, for exactly that use.
 *
 * The email address is the other half of that story and is *not* dead:
 * it moves through WorkOS's send/confirm pair below, because sign-in
 * matches on a verified address. Changing it here without telling WorkOS
 * would leave the account unable to sign in at all.
 */

interface ExtendedUserUpdate extends UserUpdate {}

const SettingsPage: React.FC<SettingsPageProps> = ({ currentPage }) => {
  const [loading, setLoading] = useState(true);
  const [ownUser, setUser] = useState<UserReturn | null>(null);
  // The sync engine's copy of this user and their company when it has
  // them (#561), or what this page last got back if that is newer.
  const selfId = getUserIdFromToken();
  const syncedUser = useSyncedUser(selfId ? String(selfId) : undefined);
  const user = fresher(syncedUser, ownUser);
  const haveSyncedUser = !!syncedUser;
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isProfileEditing, setIsProfileEditing] = useState(false);
  const [fetchedCompany, setCompany] = useState<CompanyReturn | null>(null);
  const companyId = user?.company_id ?? undefined;
  const syncedCompany = useSyncedCompany(companyId);
  const haveSyncedCompany = !!syncedCompany;
  const company = syncedCompany ?? fetchedCompany;
  // A user belongs to at most one company (`users.company_id` is a
  // single column, and emails are unique), so there is no picker here.
  // A SuperAdmin's authorization ignores company scope entirely, so the
  // banner below names the company rather than saying "your company".
  const userType = getUserTypeFromToken();

  const [formData, setFormData] = useState<ExtendedUserUpdate>({
    email: null,
    full_name: null,
  });

  const [touchedFields, setTouchedFields] = useState<{
    email?: boolean;
    full_name?: boolean;
  }>({});

  const [errors, setErrors] = useState<{
    email?: string;
    full_name?: string;
  }>({});

  useEffect(() => {
    const fetchUserAndCompany = async () => {
      const userId = getUserIdFromToken();

      if (!userId) {
        setShowError(true);
        setErrorMessage(
          'Unable to load user information. Please try logging in again.'
        );
        setLoading(false);
        return;
      }

      if (haveSyncedUser) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const userResponse = await userService.getUser({
          id: String(userId),
        });
        setUser(userResponse.data);
      } catch (err) {
        console.error('Error fetching user:', err);
        setShowError(true);
        setErrorMessage("Couldn't load user data");
      } finally {
        setLoading(false);
      }
    };

    fetchUserAndCompany();
  }, [haveSyncedUser]);

  // The company, once the user says which one it is.
  useEffect(() => {
    if (!companyId || haveSyncedCompany) return;
    let live = true;
    companyService
      .getCompany({ id: companyId })
      .then((r) => live && setCompany(r.data))
      .catch((err) => {
        console.error('Error fetching company:', err);
        setShowError(true);
        setErrorMessage("Couldn't load company data");
      });
    return () => {
      live = false;
    };
  }, [companyId, haveSyncedCompany]);

  // The form shows the current values whenever it is not being edited —
  // including a change that arrives through the sync engine.
  const currentEmail = user?.email;
  const currentName = user?.full_name;
  useEffect(() => {
    if (isProfileEditing || currentEmail === undefined) return;
    setFormData({ email: currentEmail, full_name: currentName ?? null });
  }, [isProfileEditing, currentEmail, currentName]);

  const validateField = (field: string, value: string) => {
    switch (field) {
      case 'email':
        return value && !value.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)
          ? 'Invalid email format'
          : '';
      case 'full_name':
        return !value || !value.trim() || value.trim().length < 2
          ? 'Name must be at least 2 characters (not including spaces)'
          : '';
      default:
        return '';
    }
  };

  const validateForm = () => {
    const newErrors: typeof errors = {};
    let hasErrors = false;

    if (formData.email) {
      const emailError = validateField('email', formData.email);
      if (emailError) {
        newErrors.email = emailError;
        hasErrors = true;
      }

      // Require password verification if email is being changed
      if (formData.email !== user?.email) {
      }
    }

    if (formData.full_name) {
      const nameError = validateField('full_name', formData.full_name);
      if (nameError) {
        newErrors.full_name = nameError;
        hasErrors = true;
      }
    }

    setErrors(newErrors);
    return !hasErrors;
  };

  const handleBlur = (field: string) => {
    setTouchedFields((prev) => ({
      ...prev,
      [field]: true,
    }));

    if (isProfileEditing && ['email', 'full_name'].includes(field)) {
      const value =
        (formData[field as keyof ExtendedUserUpdate] as string) || '';
      const error = validateField(field, value);
      setErrors((prev) => ({
        ...prev,
        [field]: error || undefined,
      }));
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (touchedFields[name as keyof typeof touchedFields]) {
      const error = validateField(name, value);
      setErrors((prev) => ({
        ...prev,
        [name]: error || undefined,
      }));
    }
  };

  // ── Email change ────────────────────────────────────────────────
  //
  // Two steps, because proof of control is a code sent to the address
  // being claimed. There is no password field: a password would prove
  // nothing about the new address, and since sign-in moved to WorkOS most
  // accounts no longer have one.
  //
  // Neither step writes `users.email`. WorkOS emits `user.updated` on
  // confirmation and our webhook applies it, so the two systems reconcile
  // in one place — which is why the address on screen refreshes a moment
  // after the code is accepted rather than instantly.
  const [emailStep, setEmailStep] = useState<'idle' | 'code'>('idle');
  const [newEmail, setNewEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailBusy, setEmailBusy] = useState(false);

  const resetEmailChange = () => {
    setEmailStep('idle');
    setNewEmail('');
    setEmailCode('');
    setEmailError('');
  };

  const handleSendEmailCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError('');
    setEmailBusy(true);
    try {
      await authSessionService.sendEmailChange({
        emailChangeSendRequest: { new_email: newEmail.trim() },
      });
      setEmailStep('code');
    } catch (err) {
      // The backend writes these for a person to read — "already in use",
      // "sign in through WorkOS first" — so pass them through rather than
      // replacing them with something vaguer.
      setEmailError(apiErrorMessage(err, "Couldn't start the email change."));
    } finally {
      setEmailBusy(false);
    }
  };

  const handleConfirmEmailCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError('');
    setEmailBusy(true);
    try {
      await authSessionService.confirmEmailChange({
        emailChangeConfirmRequest: { code: emailCode.trim() },
      });

      // Re-read rather than assuming. The webhook applies the change, so
      // the value here is whatever actually landed — and if it has not
      // arrived yet the user sees the old address rather than a claim
      // that turns out to be wrong.
      const userId = getUserIdFromToken();
      if (userId) {
        const refreshed = await userService.getUser({ id: String(userId) });
        setUser(refreshed.data);
        setFormData({
          email: refreshed.data.email,
          full_name: refreshed.data.full_name,
        });
      }

      resetEmailChange();
      setSuccessMessage('Your email address has been updated.');
      setShowSuccessAlert(true);
    } catch (err) {
      setEmailError(apiErrorMessage(err, "Couldn't verify that code."));
    } finally {
      setEmailBusy(false);
    }
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const userId = getUserIdFromToken();
    try {
      if (!userId) {
        setShowError(true);
        setErrorMessage(
          'Unable to load user information. Please try logging in again.'
        );
        return;
      }

      const updateData: UserUpdate = {
        email: formData.email,
        full_name: formData.full_name,
      };

      const response = await userService.updateUser({
        id: String(userId),
        userUpdate: updateData,
      });
      setUser(response.data);
      setFormData({
        email: response.data.email,
        full_name: response.data.full_name,
      });

      setSuccessMessage('Profile updated successfully');
      setShowSuccessAlert(true);
      setIsProfileEditing(false);
    } catch (err) {
      console.error('Error updating profile:', err);
      setShowError(true);
      setErrorMessage(
        "Couldn't update profile. Please check your data and try again."
      );
    }
  };

  const handleCancelProfileEdit = () => {
    setIsProfileEditing(false);
    setFormData({
      email: user?.email || null,
      full_name: user?.full_name || null,
    });
    setErrors({});
    setTouchedFields({});
  };

  const isProfileFormValid = () => {
    // Check if any field has validation errors when touched
    if (
      (touchedFields.email && errors.email) ||
      (touchedFields.full_name && errors.full_name)
    ) {
      return false;
    }

    // Check if any fields are empty or invalid length
    if (
      !formData.email?.trim() ||
      !formData.full_name?.trim() ||
      formData.full_name.trim().length < 2
    ) {
      return false;
    }

    // Check if any data has actually changed. Email is read-only in this
    // form now — it moves through the Email Address card — so the name is
    // the only field that can differ.
    if (formData.full_name === user?.full_name) {
      return false;
    }

    return true;
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <SidebarLayout
      sidebar={<AppSidebar currentPage={currentPage} />}
      navbar={<Navbar />}
    >
      <div className="max-w-4xl mx-auto">
        <Heading level={2}>Account Settings</Heading>
        <Divider className="mt-4 mb-6" />

        <ShowAlert
          alertType="success"
          alertTitle="Success"
          alertMessage={successMessage}
          isVisible={showSuccessAlert}
          onClose={() => setShowSuccessAlert(false)}
          timeout={5000}
          isInline={true}
        />

        <ShowAlert
          alertType="error"
          alertTitle="Error"
          alertMessage={errorMessage}
          isVisible={showError}
          onClose={() => setShowError(false)}
          timeout={5000}
          isInline={true}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-stone-100 dark:bg-gray-600 p-6 rounded-lg shadow h-full">
            <div className="flex justify-between items-center mb-4">
              <Heading level={3}>Profile Information</Heading>
              <button
                onClick={() => setIsProfileEditing(!isProfileEditing)}
                className="p-1 hover:bg-zinc-100 dark:hover:bg-zinc-700 rounded-full transition-colors duration-200"
              >
                {isProfileEditing ? (
                  <PencilSquareIconSolid className="h-5 w-5 text-blue-500" />
                ) : (
                  <PencilSquareIconOutline className="h-5 w-5 text-zinc-400 hover:text-blue-500 dark:text-zinc-100 dark:hover:text-blue-500" />
                )}
              </button>
            </div>

            <form onSubmit={handleProfileUpdate} className="space-y-4">
              <Fieldset>
                <FieldGroup>
                  {/* Read-only here on purpose. Sign-in matches a WorkOS-verified
                      address against this column, so writing it directly would
                      leave WorkOS holding the old one and lock the user out at
                      their next sign-in. The backend refuses it too. The supported
                      route is the Email Address card below. */}
                  <Field>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={user?.email || ''}
                      disabled
                      readOnly
                    />
                    <Description>Change your email address below.</Description>
                  </Field>
                  <Field>
                    <Label htmlFor="full_name">Full Name</Label>
                    <Input
                      id="full_name"
                      name="full_name"
                      value={formData.full_name || ''}
                      onChange={handleInputChange}
                      onBlur={() => handleBlur('full_name')}
                      disabled={!isProfileEditing}
                      data-invalid={
                        touchedFields.full_name && errors.full_name
                          ? true
                          : undefined
                      }
                    />
                    {touchedFields.full_name && errors.full_name && (
                      <ErrorMessage>{errors.full_name}</ErrorMessage>
                    )}
                  </Field>
                </FieldGroup>
              </Fieldset>
              {isProfileEditing && (
                <div className="mt-4 space-x-2">
                  <Button
                    type="submit"
                    color="sky"
                    disabled={!isProfileFormValid()}
                  >
                    Save Changes
                  </Button>
                  <Button
                    type="button"
                    color="zinc"
                    onClick={handleCancelProfileEdit}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </form>
          </div>

          <div className="bg-stone-100 dark:bg-gray-600 p-6 rounded-lg shadow h-full">
            <Heading level={3}>Email Address</Heading>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-200">
              Signing in uses this address, so changing it needs a code sent to
              the new one.
            </p>

            <ErrorAlert className="mt-4">{emailError}</ErrorAlert>

            {emailStep === 'idle' ? (
              <form onSubmit={handleSendEmailCode} className="mt-4">
                <Fieldset>
                  <FieldGroup>
                    <Field>
                      <Label htmlFor="new_email">New Email Address</Label>
                      <Input
                        id="new_email"
                        name="new_email"
                        type="email"
                        autoComplete="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                      />
                    </Field>
                  </FieldGroup>
                </Fieldset>
                <div className="mt-4">
                  <Button
                    type="submit"
                    color="sky"
                    disabled={emailBusy || !newEmail.trim()}
                  >
                    {emailBusy ? 'Sending…' : 'Send confirmation code'}
                  </Button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleConfirmEmailCode} className="mt-4">
                <Fieldset>
                  <FieldGroup>
                    <Field>
                      <Label htmlFor="email_code">Confirmation Code</Label>
                      <Description>
                        Sent to {newEmail}. Check that inbox, not this one.
                      </Description>
                      <Input
                        id="email_code"
                        name="email_code"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        value={emailCode}
                        onChange={(e) => setEmailCode(e.target.value)}
                      />
                    </Field>
                  </FieldGroup>
                </Fieldset>
                <div className="mt-4 space-x-2">
                  <Button
                    type="submit"
                    color="sky"
                    disabled={emailBusy || !emailCode.trim()}
                  >
                    {emailBusy ? 'Confirming…' : 'Confirm'}
                  </Button>
                  <Button type="button" color="zinc" onClick={resetEmailChange}>
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </div>
          {company && (
            <div className="md:col-span-1 h-full">
              <div className="bg-stone-100 dark:bg-gray-600 p-6 rounded-lg shadow">
                <Heading level={3}>Company Information</Heading>
                <Fieldset>
                  <FieldGroup>
                    <Field>
                      <Label>Company Name</Label>
                      <p className="text-gray-700 dark:text-gray-300">
                        {company.name}
                      </p>
                    </Field>
                    <Field>
                      <Label>Email</Label>
                      <p className="text-gray-700 dark:text-gray-300">
                        {company.email}
                      </p>
                    </Field>
                    <Field>
                      <Label>Phone</Label>
                      <p className="text-gray-700 dark:text-gray-300">
                        {company.phone || 'N/A'}
                      </p>
                    </Field>
                    <Field>
                      <Label>Website</Label>
                      <p className="text-gray-700 dark:text-gray-300">
                        {company.website || 'N/A'}
                      </p>
                    </Field>
                    <Field>
                      <Label>Address</Label>
                      <p className="text-gray-700 dark:text-gray-300">
                        {company.street}
                        {company.street2 ? `, ${company.street2}` : ''}
                        <br />
                        {company.city}, {company.state} {company.zip}
                        <br />
                        {company.country}
                      </p>
                    </Field>
                  </FieldGroup>
                </Fieldset>
              </div>
            </div>
          )}
        </div>

        {company && canViewLedger(userType) && (
          <>
            <Divider className="my-10" />
            <div id="billing-defaults">
              <Heading level={3}>Billing defaults</Heading>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                The billing policy a new association under{' '}
                <strong>{company.name}</strong> starts from.
              </p>
              <div className="mt-6">
                <BillingPolicyForm
                  scope="company"
                  scopeId={company.id}
                  canEdit={canManageBillingPolicy(userType)}
                />
                <Divider className="my-10" />
                <NoticeRuleList
                  scope="company"
                  scopeId={company.id}
                  canEdit={canManageBillingPolicy(userType)}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </SidebarLayout>
  );
};

export default SettingsPage;
